import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Flex } from 'antd'
import Actions from 'actions'
import $t from '../../../../i18n'
import { event, eventTypes } from 'consts/eventConsts'
import { useDebounce, useIsMobile } from 'hooks'
import { getUrlParams } from 'utils/useParamUtil'
import AutocompleteInput from '../../../common/AutocompleteInput'
import ProviderTier from '../../../common/ProviderTier/ProviderTier'
import { getProviderDisplayName } from '../../../../utils/providerSearchUtils'
import './SearchBar.css'

const getIcon = type => {
  switch (type) {
    case 'specialty':
      return (
        <i className='icon-providers-speciality autocomplete-option-icon' />
      )
    case 'provider':
      return (
        <i className='icon-providers-individual autocomplete-option-icon' />
      )
    case 'facility':
      return <i className='icon-providers-facility autocomplete-option-icon' />
    default:
      return <i className='icon-search' />
  }
}

const renderSpecialtyTier = (item, className = '') => {
  if (!item.primary_specialty && !(item.tier && item.tier.trim() !== '')) {
    return null
  }

  return (
    <Flex align='center' gap={8} className={className}>
      {item.primary_specialty && (
        <div className='autocomplete-option-specialty-badge'>
          {item.primary_specialty}
        </div>
      )}
      {item.tier && item.tier.trim() !== '' && (
        <div className='autocomplete-tier-wrapper'>
          <ProviderTier tier={item.tier} iconOnly={true} />
        </div>
      )}
    </Flex>
  )
}

const SearchBar = ({
  query,
  setQuery,
  onSearch: onSearchProp,
  onClickSearchBar = () => {},
  onClickSuggestion = () => {},
  onClickSearchButton = () => {},
  viewMode = 'dropdown', // 'dropdown' or 'list'
  isReadOnly = false
}) => {
  const dispatch = useDispatch()
  const [isSearchDisabled, setIsSearchDisabled] = useState(true)
  const isMobile = useIsMobile()
  const {
    providersSuggestions,
    isProvidersSuggestionsLoading,
    providersSearchQuery,
    providersSearchLocation
  } = useSelector(state => state.providers)

  const noResultsLocation = useMemo(() => {
    if (providersSearchLocation?.city && providersSearchLocation?.state) {
      return `${providersSearchLocation.city}, ${providersSearchLocation.state}`
    }
    return providersSearchLocation?.zipcode ?? null
  }, [providersSearchLocation])

  useEffect(() => {
    if (providersSearchQuery !== query) {
      const queryParams = getUrlParams()
      if (!providersSearchQuery) {
        updateQuery(queryParams?.query ?? '')
      } else {
        setQuery(providersSearchQuery)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providersSearchQuery])

  const updateQuery = useCallback(
    query => {
      dispatch(
        Actions.createAction(Actions.PROVIDERS_SEARCH_QUERY_UPDATED, query)
      )
      setQuery(query)
    },
    [dispatch]
  )

  const debouncedQuery = useDebounce(query, 500)

  useEffect(() => {
    setIsSearchDisabled(debouncedQuery.length < 2)
  }, [debouncedQuery])

  useEffect(() => {
    if (debouncedQuery.length < 2) {
      return
    }

    dispatch(
      Actions.createAction(Actions.PROVIDERS_SUGGESTIONS_REQUESTED, {
        query: debouncedQuery,
        providersSearchLocation
      })
    )
  }, [debouncedQuery, dispatch, providersSearchLocation])

  const handleSearch = query => {
    if (query.length < 2) {
      dispatch(
        Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
          type: eventTypes.formEvent,
          eventName: event.formEvent.invalid,
          data: query,
          element: 'Search providers'
        })
      )
      return
    }

    dispatch(
      Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
        type: eventTypes.formEvent,
        eventName: event.formEvent.submit,
        data: query,
        element: 'Search providers'
      })
    )

    updateQuery(query)

    if (onSearchProp) {
      onSearchProp(query)
      onClickSearchButton()
    }
  }

  const handleSuggestionSelect = item => {
    handleSearch(item.name)
    onClickSuggestion()
  }

  const renderSuggestionItem = (item, type) => {
    return (
      <div
        key={`${type}-${item.id || item.name}`}
        onClick={() => handleSuggestionSelect(item)}
        className='mobile-suggestion-item'
      >
        <div className='autocomplete-option-content'>
          <Flex
            align='center'
            justify='space-between'
            className='autocomplete-option-header'
          >
            <Flex align='center' gap={12}>
              {getIcon(type)}
              <div
                className='autocomplete-option-name'
                dangerouslySetInnerHTML={{
                  __html:
                    type === 'specialty'
                      ? item.highlighted_name || item.name
                      : getProviderDisplayName(item)
                }}
              />
            </Flex>
            {renderSpecialtyTier(item)}
          </Flex>
        </div>
      </div>
    )
  }

  const renderAutocompleteOption = item => {
    const { type } = item
    return (
      <div className='autocomplete-option-content'>
        <Flex
          align='center'
          justify='space-between'
          className='autocomplete-option-header'
        >
          <Flex align='center' gap={12}>
            {getIcon(type)}
            <div
              className='autocomplete-option-name'
              dangerouslySetInnerHTML={{
                __html:
                  type === 'specialty'
                    ? item.highlighted_name || item.name
                    : getProviderDisplayName(item)
              }}
            />
          </Flex>
          {renderSpecialtyTier(item)}
        </Flex>
      </div>
    )
  }

  const optionGroups = useMemo(() => {
    if (!providersSuggestions) return []
    const groups = []
    if (providersSuggestions.specialties?.length > 0) {
      groups.push({
        label: $t('searchProviders.groupTitles.specialties'),
        options: providersSuggestions.specialties.map(s => ({
          ...s,
          type: 'specialty'
        }))
      })
    }
    if (providersSuggestions.individuals?.length > 0) {
      groups.push({
        label: $t('searchProviders.groupTitles.individuals'),
        options: providersSuggestions.individuals.map(i => ({
          ...i,
          type: 'provider'
        }))
      })
    }
    if (providersSuggestions.facilities?.length > 0) {
      groups.push({
        label: $t('searchProviders.groupTitles.facilities'),
        options: providersSuggestions.facilities.map(f => ({
          ...f,
          type: 'facility'
        }))
      })
    }
    return groups
  }, [providersSuggestions])

  const renderSuggestionsList = () => {
    if (!providersSuggestions || query.length < 2) {
      return null
    }

    const allSuggestions = []

    if (providersSuggestions.specialties?.length > 0) {
      allSuggestions.push(
        <div key='specialties-header' className='suggestions-group-header'>
          {$t('searchProviders.groupTitles.specialties')}
        </div>
      )
      providersSuggestions.specialties.forEach(item => {
        allSuggestions.push(renderSuggestionItem(item, 'specialty'))
      })
    }

    if (providersSuggestions.individuals?.length > 0) {
      allSuggestions.push(
        <div key='individuals-header' className='suggestions-group-header'>
          {$t('searchProviders.groupTitles.individuals')}
        </div>
      )
      providersSuggestions.individuals.forEach(item => {
        allSuggestions.push(renderSuggestionItem(item, 'provider'))
      })
    }

    if (providersSuggestions.facilities?.length > 0) {
      allSuggestions.push(
        <div key='facilities-header' className='suggestions-group-header'>
          {$t('searchProviders.groupTitles.facilities')}
        </div>
      )
      providersSuggestions.facilities.forEach(item => {
        allSuggestions.push(renderSuggestionItem(item, 'facility'))
      })
    }

    return <div className='mobile-suggestions-container'>{allSuggestions}</div>
  }

  return (
    <div onClick={() => onClickSearchBar(isMobile)}>
      <div className='search-bar-container'>
        <div className='search-input-container'>
          <AutocompleteInput
            value={query}
            placeholder={$t('searchProviders.searchFields.search')}
            onInputChange={e => setQuery(e.target.value)}
            onSelect={handleSuggestionSelect}
            onSearch={handleSearch}
            optionGroups={optionGroups}
            renderOption={renderAutocompleteOption}
            isLoading={isProvidersSuggestionsLoading}
            useSearchInput={isMobile}
            onSearchButtonClick={() => handleSearch(query)}
            isSearchDisabled={isSearchDisabled}
            isReadOnly={isReadOnly}
            noResultsLocation={noResultsLocation}
            viewMode={viewMode}
            renderOptionsList={renderSuggestionsList}
          />
          {viewMode === 'dropdown' && (
            <div className='search-provider-button-container'>
              <Button
                className='search-provider-button trackable'
                type='primary'
                data-name='search-provider-button'
                onClick={() => handleSearch(query)}
                disabled={isSearchDisabled || isReadOnly}
              >
                <i className='icon-search' />
                <span className='button-text'>
                  {$t('providerPage.searchFields.search')}
                </span>
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default SearchBar
